import { NavigationProperties } from '../../interfaces';
import { PermutiveConsent } from './consent';
import { IdentityType } from './identity';

/**
 * Permutive-shaped event framework (Mosaic Phase 6/7).
 *
 * Produces the Permutive event envelope Mosaic ingests:
 *   { id, identity_type, name, properties, segments, session_id, time,
 *     user_id, view_id }
 *
 * This module is the foundation for the individual Mosaic events (Pageview,
 * LinkClick, SlotClicked, SlotViewable, ...), which supply their own
 * event-specific `properties`. The common `client` block is shared by all
 * events and is built here; the shared `consent` block comes from
 * `mosaic/consent`.
 */

/** Discriminator used by the Doppler receive filter (DAP-114773) to route
 * Mosaic events to the dedicated S3 output. Stripped before write to S3. */
export const MOSAIC_EVENT_TYPE = 'mosaic';

export type PermutiveClient = {
  domain: string;
  referrer: string;
  title: string;
  type: string;
  url: string;
  user_agent: string;
};

export type PermutiveEventProperties = {
  client?: PermutiveClient;
  consent?: PermutiveConsent;
  [key: string]: unknown;
};

export type PermutiveEvent = {
  id: string;
  identity_type: IdentityType | null;
  name: string;
  properties: PermutiveEventProperties;
  segments: number[];
  session_id: string;
  time: string;
  user_id: string;
  view_id: string;
};

/** The identifiers resolved from PRISM state for a single event. */
export type PermutiveIdentity = {
  /** Maps to the PRISM event id. */
  id: string;
  /** Which tier of the waterfall `user_id` came from; null if none resolved. */
  identity_type: IdentityType | null;
  /** Maps to the PRISM session id. */
  session_id: string;
  /** Resolved via the user_id waterfall (Bolt user id > device id > WMUKID). */
  user_id: string;
  /** Per-pageview UUID. */
  view_id: string;
};

/** Object actually enqueued/sent to the receive endpoint: the Permutive event
 * plus a discriminator the filter uses to route it. */
export type MosaicEventEnvelope = PermutiveEvent & { eventType: typeof MOSAIC_EVENT_TYPE };

/**
 * Generate or normalize an ISO-8601 timestamp.
 *
 * @param timestamp optional UNIX time (number) or date string
 */
export function getPermutiveTime(timestamp?: number | string): string {
  if (timestamp === null || timestamp === undefined) {
    return new Date().toISOString();
  }
  return new Date(timestamp).toISOString();
}

/**
 * Build the common Permutive `client` block shared by all editorial web events.
 *
 * @param nav PRISM navigation properties for the current page
 * @param userAgent the browser user agent
 * @param domain the page host (e.g. "foodnetwork.co.uk")
 */
export function buildClientBlock(
  nav: Partial<NavigationProperties>,
  userAgent: string,
  domain: string,
): PermutiveClient {
  return {
    domain: domain || '',
    referrer: nav.referrer || '',
    title: nav.title || '',
    type: 'web',
    url: nav.url || '',
    user_agent: userAgent || '',
  };
}

/**
 * Assemble a Permutive-shaped event envelope from resolved identity and
 * event-specific properties.
 *
 * `segments` is always emitted empty; Mosaic populates it downstream.
 */
export function buildPermutiveEvent(args: {
  name: string;
  properties?: PermutiveEventProperties;
  identity: PermutiveIdentity;
  time?: number | string;
}): PermutiveEvent {
  const { name, properties, identity, time } = args;
  return {
    id: identity.id,
    identity_type: identity.identity_type,
    name,
    properties: properties || {},
    segments: [],
    session_id: identity.session_id,
    time: getPermutiveTime(time),
    user_id: identity.user_id,
    view_id: identity.view_id,
  };
}
